/* import __COLOCATED_TEMPLATE__ from './optimize.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';

import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { cached } from 'tracked-toolbox';
import type IntlService from 'ember-intl/services/intl';
import { indexBy } from 'underscore';
import {
  jobs,
  jobGroups,
  jobGroupObjects,
  type JobId,
} from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { type SummaryTab } from 'embercom/components/reporting/automation/ai-insights/optimize/conversation-explore-panel';

interface Args {}

export default class Optimize extends Component<Args> {
  @service declare appService: any;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare permissionsService: any;

  @tracked selectedJobGroup: keyof typeof jobGroups = 'improveAnswers';
  @tracked selectedJobId: JobId = 'contentGaps';

  @tracked showSidePanel = false;
  @tracked selectedConversationId = 0;
  @tracked filters: LogicalFilter | undefined = undefined;
  @tracked attributeValuesToExplore: string[] = [];
  @tracked summaryType: SummaryTab | undefined = undefined;
  @tracked summaryText: string | undefined = undefined;

  get isStandaloneApp() {
    return this.appService.app.isStandaloneApp;
  }

  get serializedReport() {
    return {
      id: 'optimize',
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }

  @cached
  get jobs() {
    return indexBy(jobs(this.intl), (job) => job.id);
  }

  get availableJobIds() {
    return jobGroups[this.selectedJobGroup];
  }

  get availableJobs() {
    return this.availableJobIds.map((id) => this.jobs[id]);
  }

  get selectedJob() {
    return this.jobs[this.selectedJobId];
  }

  get jobGroupObjects() {
    return jobGroupObjects;
  }

  get selectedJobGroupObject() {
    return this.jobGroupObjects.find(
      (jobGroupObject) => jobGroupObject.id === this.selectedJobGroup,
    );
  }

  @action
  changeJobGroup(jobGroup: keyof typeof jobGroups) {
    this.selectedJobGroup = jobGroup;
    this.selectedJobId = this.availableJobIds[0];
    this.trackAnalyticsEvent('job-group-changed', jobGroup);
  }

  @action
  trackAnalyticsEvent(action: string, object: string, extraContext: Record<string, any> = {}) {
    this.intercomEventService.trackAnalyticsEvent({
      place: 'analyze/optimize-fin-reasons',
      action,
      object,
      ...extraContext,
    });
  }

  @action
  changeJob(jobId: JobId) {
    this.selectedJobId = jobId;
    this.trackAnalyticsEvent('job-changed', jobId);
  }

  get attributeIds() {
    return [this.selectedJob.attributeId];
  }

  @action
  onShowConversationExplorer(
    selectedConversationId: number | null,
    filters: LogicalFilter,
    attributeValuesToExplore: string[],
    summaryType: SummaryTab,
    summaryText: string,
  ) {
    if (this.canAccessDrillIn) {
      this.selectedConversationId = selectedConversationId || 0;
      this.filters = filters;
      this.attributeValuesToExplore = attributeValuesToExplore;
      this.summaryType = summaryType;
      this.summaryText = summaryText;
      this.showSidePanel = true;
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
    }
  }

  get canAccessDrillIn() {
    return this.permissionsService.currentAdminCan('can_reporting__drillin__access');
  }

  @action
  closeConversationExplorer() {
    this.showSidePanel = false;
    this.selectedConversationId = 0;
    this.filters = undefined;
    this.attributeValuesToExplore = [];
    this.summaryType = undefined;
    this.summaryText = undefined;
    this.trackAnalyticsEvent('conversation-explorer-closed', 'conversation-explorer');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::Optimize': typeof Optimize;
  }
}
